import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Checkbox, Grid, List, ListItem, ListItemText, LinearProgress, styled, Typography } from '@mui/material';

import { FormattedDateTimeRange, Icon, useSettings, Utils } from 'app/main/components';
import { DetailsSectionWrapper } from 'app/main/components/page-layouts';

const StyledCheckbox = styled(Checkbox)(({ checked, theme }) => ({
	'&.Mui-disabled': {
		color: checked ? theme.palette.success.main : 'inherit'
	}
}));

function OrdersPromosDetailsSectionGroupRows(props: { promoGroupRow: any; isLast?: boolean }) {
	const { t } = useSettings();

	return (
		<ListItem divider={!props.isLast}>
			<ListItemText
				primary={
					props.promoGroupRow.item_title
						? `${props.promoGroupRow.orpw_quantity}x [${props.promoGroupRow.item_erp_id}] ${props.promoGroupRow.item_title}`
						: props.promoGroupRow.orpw_note || (
								<FormattedMessage
									id="ordersPromosTypeDiscount.label"
									defaultMessage={t('ORDERS_PROMOS_TYPE_DISCOUNT_LABEL')}
									values={{
										value: (
											<FormattedNumber
												value={props.promoGroupRow.orpw_discount / 100}
												style="percent"
											/>
										)
									}}
								/>
							)
				}
			/>
		</ListItem>
	);
}

function OrdersPromosDetailsSectionGroup(props: {
	promoGroup: any;
	orderUid: string;
	orderTotValueTaxExcl?: number;
	promoUid: string;
	isEditable?: boolean;
}) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [checked, setChecked] = React.useState(!!props.promoGroup.active);

	const progress = ((props.orderTotValueTaxExcl || 0) * 100) / props.promoGroup.tot_value_from;
	const isReached =
		!props.promoGroup.tot_value_from || props.promoGroup.tot_value_from <= (props.orderTotValueTaxExcl || 0);
	const isUnderMax =
		!props.promoGroup.tot_value_to || props.promoGroup.tot_value_to > (props.orderTotValueTaxExcl || 0);

	function editPromoConfirm() {
		Utils.executeStored({
			action: 'st_orders_promos_rows_confirms_manager',
			dispatch,
			data: {
				ords_uid: props.orderUid,
				orpm_uid: props.promoUid,
				orpm_rows: props.promoGroup.orpm_rows.map(row => row.orpw_uid),
				delete: checked
			},
			successHandler: response => {
				setChecked(state => !state);
				dispatch(Utils.alertSuccessMsg(t));
			}
		});
	}

	return (
		<Grid size={12}>
			<DetailsSectionWrapper
				title={
					<>
						<Typography variant="subtitle2">
							<FormattedMessage
								id="ordersPromosGroupItem.title"
								defaultMessage={t('ORDERS_PROMOS_GROUP_ITEM_TITLE')}
								values={{
									value: (
										<FormattedNumber
											value={props.promoGroup.tot_value_from}
											style="currency"
											currency="EUR"
										/>
									)
								}}
							/>
						</Typography>
						<Typography variant="subtitle2">
							<FormattedMessage
								id="ordersPromosGroupItem.title2"
								defaultMessage={t('ORDERS_PROMOS_GROUP_ITEM_TITLE2')}
								values={{
									value: props.promoGroup.tot_value_to ? (
										<FormattedNumber
											value={props.promoGroup.tot_value_to}
											style="currency"
											currency="EUR"
										/>
									) : (
										'--'
									)
								}}
							/>
						</Typography>
					</>
				}
				headerAction={
					isReached && (
						<StyledCheckbox
							checked={checked && isUnderMax}
							icon={<Icon icon={isUnderMax ? 'checkbox-blank-circle-outline' : 'close-circle'} />}
							checkedIcon={<Icon icon="check-circle" />}
							color={checked ? 'success' : 'default'}
							disabled={!props.isEditable || !isUnderMax}
							onClick={editPromoConfirm}
						/>
					)
				}
				disablePadding
			>
				{!isReached && (
					<Typography className="px-8 text-end" variant="subtitle2">
						<FormattedMessage
							id="ordersPromosGroupItem.progress"
							defaultMessage={t('ORDERS_PROMOS_GROUP_ITEM_SUBHEADER')}
							values={{
								value: (
									<FormattedNumber
										value={props.promoGroup.tot_value_from - (props.orderTotValueTaxExcl || 0)}
										style="currency"
										currency="EUR"
									/>
								)
							}}
						/>
					</Typography>
				)}
				<LinearProgress
					variant="determinate"
					color={isReached && isUnderMax ? 'success' : 'inherit'}
					value={progress > 100 ? 100 : progress}
				/>
				<List disablePadding>
					{props.promoGroup.orpm_rows?.map((promoGroupRow, index) => (
						<OrdersPromosDetailsSectionGroupRows
							key={promoGroupRow.orpw_uid}
							promoGroupRow={promoGroupRow}
							isLast={index === props.promoGroup.orpm_rows.length - 1}
						/>
					))}
				</List>
			</DetailsSectionWrapper>
		</Grid>
	);
}

export default function getOrdersPromosDetailsSections({
	orderUid,
	orderStatusId,
	orderTotValueTaxExcl = 0
}: {
	orderUid: string;
	orderStatusId?: number;
	orderTotValueTaxExcl?: number;
}) {
	const dispatch = useDispatch();

	const isEditable = orderStatusId === 1;

	const [promos, setPromos] = React.useState<any[]>([]);

	React.useEffect(() => {
		if (orderUid) {
			Utils.executeStored({
				action: 'st_orders_promos_list_active',
				params: { ords_uid: orderUid },
				dispatch,
				successHandler: response => {
					setPromos(response.data || []);
				}
			});
		}
	}, [dispatch, orderUid]);

	return promos.map(promo => (
		<Grid key={promo.orpm_uid} size={12}>
			<DetailsSectionWrapper
				title={promo.orpm_title}
				subheader={<FormattedDateTimeRange from={promo.orpm_d_from} to={promo.orpm_d_to} />}
			>
				<Grid container spacing={2}>
					{promo.orpm_groups?.map(promoGroup => (
						<OrdersPromosDetailsSectionGroup
							key={promoGroup.tot_value_from}
							promoGroup={promoGroup}
							orderUid={orderUid}
							orderTotValueTaxExcl={orderTotValueTaxExcl}
							promoUid={promo.orpm_uid}
							isEditable={isEditable}
						/>
					))}
				</Grid>
			</DetailsSectionWrapper>
		</Grid>
	));
}
